import { useState } from "react";
import { ChartInfo } from "@/components/ChartInfo";
import { Headline } from "@/components/Headline";
import { PlanPage } from "../PlanPage";

/**
 * Achievements' six views (prototype v45 `ACH_VIEWS`): each compares now with the line the household
 * was on when they joined. None can be drawn yet: there's no stored history of actual net worth, no
 * plan kept from the signup date, and no bank transactions (docs/design/DEVIATIONS.md, "Views the
 * plan can't fill"), so every view is the prototype's "waiting" panel naming what it needs.
 */
type AchView = {
  k: string;
  n: string;
  h: string;
  s: string;
  /** What it waits on: the engine's signup snapshot or history, or the household's accounts. */
  on: "engine" | "accounts";
  needs: string;
};

export const ACH_VIEWS: AchView[] = [
  {
    k: "nw",
    n: "Net worth",
    h: "Net worth since you joined",
    s: "Where you actually are, against the line you were on the day you signed up.",
    on: "engine",
    needs:
      "Needs a record of your actual net worth over time, and the plan as it stood on the signup date. Neither is kept yet.",
  },
  {
    k: "save",
    n: "What you keep",
    h: "What you keep each month",
    s: "Money in less money out, month by month, from the twelve months of feed we hold.",
    on: "accounts",
    needs:
      "Needs twelve months of your bank transactions, which come from connecting your accounts. Connecting isn’t available yet.",
  },
  {
    k: "pri",
    n: "Priorities",
    h: "Priorities funded since you joined",
    s: "How much of each goal was funded on the trajectory you were on, against now.",
    on: "engine",
    needs: "Needs the funding percentages the engine computed at signup.",
  },
  {
    k: "ret",
    n: "Retirement",
    h: "Retirement since you joined",
    s: "The nest egg you were on course for at signup, against the one you are on course for now.",
    on: "engine",
    needs: "Needs the plan as it stood on the signup date.",
  },
  {
    k: "debt",
    n: "Debt",
    h: "Debt since you joined",
    s: "What you owed and when it was due to clear at signup, against both today.",
    on: "engine",
    needs: "Needs the debt schedule as it stood then.",
  },
  {
    k: "spend",
    n: "Spending",
    h: "Spending since you joined",
    s: "Your monthly average in the twelve months before you joined, against the twelve months since.",
    on: "accounts",
    needs:
      "Needs a full year of your bank transactions either side of the signup date, which come from connecting your accounts.",
  },
];

/** The prototype's "waiting" panel: nothing shown rather than something invented. */
function Waiting({ view }: { view: AchView }) {
  return (
    <div className="achpend" data-testid={`ach-waiting-${view.k}`}>
      <div className="pi" aria-hidden="true">
        &#9671;
      </div>
      <b>{view.on === "engine" ? "Waiting on the engine" : "Waiting on your accounts"}</b>
      <p>{view.needs}</p>
      <p className="sm">
        Nothing is shown here rather than something invented. It fills in the moment that data
        exists.
      </p>
    </div>
  );
}

function AchievementsBody() {
  const [k, setK] = useState(ACH_VIEWS[0].k);
  const view = ACH_VIEWS.find((v) => v.k === k) ?? ACH_VIEWS[0];
  return (
    <>
      <Headline
        id="hd-ach"
        cards={[
          {
            k: "Net worth",
            v: "Not kept yet",
            pending: true,
            s: "since you joined: needs a record of your net worth over time",
            testId: "ach-networth",
          },
          {
            k: "Debt paid off",
            v: "Not kept yet",
            pending: true,
            s: "off your balances since you joined: needs your balances as they stood then",
            testId: "ach-debt",
          },
          {
            k: "Priorities funded",
            v: "Not kept yet",
            pending: true,
            s: "fully paid for since you joined: needs the plan as it stood on the signup date",
            testId: "ach-priorities",
          },
          {
            k: "Actions completed",
            v: "Not recorded yet",
            pending: true,
            s: "your check-ins update the plan, but what you ticked off isn’t kept as a list yet",
            testId: "ach-actions",
          },
        ]}
      />
      <div
        className="cfvtabs"
        id="achtabs"
        role="tablist"
        aria-label="Achievement views"
        style={{ marginBottom: 0 }}
      >
        {ACH_VIEWS.map((v) => (
          <button
            key={v.k}
            type="button"
            role="tab"
            aria-selected={v.k === k}
            aria-controls="ach-canvas"
            className={`cfvtab${v.k === k ? " on" : ""}`}
            onClick={() => setK(v.k)}
            data-testid={`ach-tab-${v.k}`}
          >
            {v.n}
          </button>
        ))}
      </div>
      <div className="card pad cfcard" style={{ marginBottom: 16 }}>
        <div className="cf-bar" style={{ marginBottom: 14 }}>
          <div>
            <h2 style={{ marginBottom: 4 }} data-testid="ach-h2">
              {view.h}
            </h2>
            <p className="mut" style={{ margin: 0, maxWidth: "68ch" }}>
              {view.s}
            </p>
          </div>
        </div>
        <div id="ach-canvas" className="gwrap" role="tabpanel">
          <ChartInfo k="ach-canvas" />
          <Waiting view={view} />
        </div>
      </div>
    </>
  );
}

/** Achievements (prototype v41/v45, was History): one chart with a six-way toggle. */
export function AchievementsPage() {
  return (
    <PlanPage title="pages.history.title" sub="pages.history.sub" testId="achievements">
      {() => <AchievementsBody />}
    </PlanPage>
  );
}
